import * as proj from './projections';
import translator from './translation';

/**
 * Ensemble de helper handlebar
 */
Handlebars.registerHelper('dmm', (deg) => proj.DMM(deg));

Handlebars.registerHelper('dms', (deg) => proj.DMS(deg));

Handlebars.registerHelper('utm', (lat, lng) => proj.UTM(lat, lng));

Handlebars.registerHelper('dmm_cardinal', (lat, lng) => proj.DMM_Cardinal(lat, lng));

Handlebars.registerHelper('L93', (lat, lng, axis) => {
    const l93 = proj.degrees2L93(lat, lng);

    if (axis === 'x') {
        return l93.x.toFixed(0);
    }
    if (axis === 'y') {
        return l93.y.toFixed(0);
    }

    return [`${l93.x.toFixed(0)},${l93.y.toFixed(0)}`];
});

Handlebars.registerHelper('concat', (...args) => {
    args.pop();
    return args.join('');
});

Handlebars.registerHelper('translate', (key, defaultTranslation) => {
    const translated = translator.t(key);

    if (translated === key && typeof defaultTranslation === 'string') {
        return defaultTranslation;
    }

    return translated;
});

Handlebars.registerHelper('keys', (obj, options) => {
    const exclude = options.hash.exclude?.split(';') ?? [];
    
    return Object.keys(obj ?? {})
        .filter(k => !exclude.includes(k));
});

Handlebars.registerHelper('short_city', (com) => com
    .split(" - ")[1]
    .substring(0,8)
);

Handlebars.registerHelper('communeOnly', (com) => {
    if (typeof com !== 'string') {
        return '';
    }

    return com
        .replace(/^\s*\d{5}\s*/, '')
        .replace(/^\s*-\s*/, '')
        .trim()
        .toUpperCase()
        .replace(/[\s']+/g, '-');
});

// Helper pour comparer deux valeurs
Handlebars.registerHelper('eq', function(a, b) {
    return a === b;
});

// Helper pour les opérations logiques OR
Handlebars.registerHelper('or', function() {
    return Array.prototype.slice.call(arguments, 0, -1).some(Boolean);
});

Handlebars.registerHelper('equals', (value1, value2) => value1 === value2);

Handlebars.registerHelper('hour', (datetime) => (new Date(datetime)).toLocaleTimeString()
    .split(":")
    .slice(0,2)
    .join(":")
);

Handlebars.registerHelper('date', (datetime) => (new Date(datetime)).toLocaleDateString('fr-FR'));

// Vrai si la date fournie correspond au jour courant
Handlebars.registerHelper('isToday', (datetime) => {
    const d = new Date(datetime);

    if (isNaN(d.getTime())) {
        return false;
    }

    const now = new Date();

    return d.getFullYear() === now.getFullYear()
        && d.getMonth() === now.getMonth()
        && d.getDate() === now.getDate();
});

Handlebars.registerHelper('object', (obj) => JSON.stringify(obj));

Handlebars.registerHelper('int', (obj) => parseInt(obj));

Handlebars.registerHelper('inArray', (needle, haystack, propertyName) => {
    return haystack.find((elem) => {
        if (propertyName && typeof propertyName === 'string') {
            return elem[propertyName] === needle;
        }
        
        return elem === needle;
    })});

Handlebars.registerHelper('notEmpty', (obj) => obj?.length > 0);

Handlebars.registerHelper('and', (cond1, cond2) => cond1 && cond2);

Handlebars.registerHelper('notDefined', (obj) => obj === undefined || obj === null);

Handlebars.registerHelper('decimal', (data, decimal) => {
    if (!['string', 'number'].includes(typeof data)) {
        return "NaN";
    }

    if(typeof data === 'string') {
        return parseFloat(data).toFixed(decimal);
    }

    return data?.toFixed(decimal);
});

Handlebars.registerHelper('isEmpty', function(value) {
    if (!value) {
        return true;
    }

    if (Array.isArray(value)) {
        return value.length === 0;
    }

    if (typeof value === 'object') {
        return Object.keys(value).length === 0;
    }

    if (typeof value === 'string') {
        return value.trim().length === 0;
    }

    return false;
});

Handlebars.registerHelper('gt', function(a, b) {
    return a > b;
});

Handlebars.registerHelper('getdevice', function(userAgent) {
    return userAgent.match(/Android|Iphone/i)?.[0] ?? '';
});

Handlebars.registerHelper('url', function(string) {
    return encodeURIComponent(string);
});

Handlebars.registerHelper('decodeURIComponent', function (value) {
  if (!value) {
    return '';
  }

  try {
    return decodeURIComponent(value);
  } catch (error) {
    return value;
  }
});

Handlebars.registerHelper('decodeUnicode', function (value) {
  if (value == null) return '';

  return String(value)
    // Entités hexadécimales HTML : &#xE9;
    .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) =>
      String.fromCodePoint(parseInt(hex, 16))
    )
    // Entités décimales HTML : &#233;
    .replace(/&#(\d+);/g, (_, dec) =>
      String.fromCodePoint(parseInt(dec, 10))
    )
    // Séquences Unicode littérales : \u00E9
    .replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) =>
      String.fromCodePoint(parseInt(hex, 16))
    );
});

Handlebars.registerHelper('contains', function(needle, haystack, options) {
  const searchedText = String(needle ?? '');
  const sourceText = String(haystack ?? '');

  return sourceText.includes(searchedText) ? options.fn(this) : options.inverse(this);
});

Handlebars.registerHelper('jco_gendloc_parser', function(value) {
  const sourceText = String(value ?? '');

  const coordinatesMatch = sourceText.match(/L\.latLng\(\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*\)/);
  if (!coordinatesMatch) {
    return sourceText;
  }

  const latitude = Number(coordinatesMatch[1]).toFixed(5);
  const longitude = Number(coordinatesMatch[2]).toFixed(5);

  const precisionMatch = sourceText.match(/-\s*(\d+)\s*m\b/);
  const zoomMatch = sourceText.match(/setView\([^)]*\)\s*,\s*(\d+)\s*\)/);
  const precision = precisionMatch?.[1] ?? zoomMatch?.[1] ?? '';

  return `Gendloc ${latitude},${longitude}${precision ? ` P: ${precision}m` : ''}`;
});

Handlebars.registerHelper('canEditOverlay', function(user) {
    const parsedUser = typeof user === 'string' ? JSON.parse(user) : user;
    const groups = parsedUser?.groups ?? [];

    return groups.includes('niveau1') || groups.includes('niveau2');
});

Handlebars.registerHelper('featureByType', function (features, type) {
  if (!Array.isArray(features)) {
    return null;
  }

  return features.find((feature) => {
    const fType = feature?.properties?.type;
    if (typeof fType === 'object' && fType !== null) {
      return (fType.id != null && Number(fType.id) === Number(type)) || fType.name === type;
    }
    return (fType != null && Number(fType) === Number(type)) || fType === type;
  });
});